import React, { useState, useEffect } from "react";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import PageHeader from "@iso/components/utility/pageHeader";
import { Button, Dropdown, Menu, Popconfirm, Table, Tag, message, Modal } from "antd";
import EntraSsoWrapper from "./EntraSso.style";
import {
  PlusOutlined,
  EditOutlined,
  DownOutlined,
  DeleteOutlined,
  LinkOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import {
  entraSsoService,
  corporateGroupService,
  propertyRoleService,
} from "@iso/services";
import EntraSsoModal from "./EntraSsoModal";
import { buildAdminConsentUrl } from "@iso/lib/auth/msal";

const EntraSso = () => {
  const [dataSource, setDataSource] = useState([]);
  const [visibleModal, setVisibleModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState({});
  const [corporateGroups, setCorporateGroups] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);

  useEffect(() => {
    getConfigs();
    corporateGroupService.getList().then((res) => {
      if (res && res.code === "0000") {
        setCorporateGroups(res.corporate_groups || []);
      }
    });
    entraSsoService.getProperties().then((res) => {
      if (res) {
        setProperties(res.properties || []);
      }
    });
    propertyRoleService.getRoleTypes().then((res) => {
      if (res) {
        setRoleTypes(res.role_types || []);
      }
    });
  }, []);

  const getConfigs = () => {
    entraSsoService.get().then((res) => {
      if (res && res.code === "0000") {
        setDataSource(res.configs || []);
      }
    });
  };

  const roleLabel = (value) => {
    const match = roleTypes.find((r) => r.value === value);
    return match ? match.label : value;
  };

  const columns = [
    {
      title: "Corporate Group",
      dataIndex: "corporate_group_name",
      key: "corporate_group_name",
      fixed: "left",
      render: (text, record) => text || `#${record.corporate_group_id}`,
    },
    {
      title: "Entra Tenant ID",
      dataIndex: "entra_tenant_id",
      key: "entra_tenant_id",
    },
    {
      title: "Allowed Email Domains",
      dataIndex: "allowed_email_domains",
      key: "allowed_email_domains",
      render: (domains) =>
        domains && domains.length
          ? domains.map((d) => <Tag key={d}>{d}</Tag>)
          : "—",
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled) =>
        enabled ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>,
    },
    {
      title: "Auto-provision",
      dataIndex: "auto_provision",
      key: "auto_provision",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Enforce SSO",
      dataIndex: "enforce_sso",
      key: "enforce_sso",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Default Role",
      dataIndex: "default_role_type",
      key: "default_role_type",
      render: (v) => (v ? roleLabel(v) : "—"),
    },
    {
      title: "Default Property",
      dataIndex: "default_property_name",
      key: "default_property_name",
      render: (v) => v || "—",
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <Dropdown overlay={menu(record)} trigger={["click"]}>
          <Button className="btn-success ant-btn-primary">
            <span>Actions</span> <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const menu = (config) => (
    <Menu onClick={handleMenuClick.bind(this, config)}>
      <Menu.Item key="editConfig" icon={<EditOutlined />}>
        Edit
      </Menu.Item>
      <Menu.Item key="consentLink" icon={<LinkOutlined />}>
        Consent link
      </Menu.Item>
      <Menu.Item key="testConn" icon={<ApiOutlined />}>
        Test connection
      </Menu.Item>
      <Popconfirm
        placement="bottomRight"
        title="Remove this SSO configuration? Users in this tenant will no longer be able to sign in with Microsoft."
        onConfirm={() => {
          confirmRemove(config.id);
        }}
        okText="Yes"
        cancelText="No"
        className="delete-button"
      >
        <a style={{ color: "rgba(0, 0, 0, 0.65)", marginLeft: "11px" }}>
          {<DeleteOutlined />}&nbsp;&nbsp;Remove
        </a>
      </Popconfirm>
    </Menu>
  );

  const handleMenuClick = (config, e) => {
    if (e.key === "editConfig") {
      setEditingConfig(config);
      setVisibleModal(true);
    } else if (e.key === "consentLink") {
      showConsentLink(config);
    } else if (e.key === "testConn") {
      runTest(config);
    }
  };

  const showConsentLink = (config) => {
    const url = buildAdminConsentUrl(config.entra_tenant_id);
    // writeText returns a promise — a rejection (no focus, denied permission)
    // must flip the dialog text rather than silently claiming success.
    const copy =
      navigator.clipboard && navigator.clipboard.writeText
        ? navigator.clipboard
            .writeText(url)
            .then(() => true)
            .catch((e) => {
              console.error("Clipboard write failed:", e);
              return false;
            })
        : Promise.resolve(false);

    copy.then((copied) => {
      Modal.info({
        title: "Admin-consent link",
        width: 640,
        content: (
          <div>
            <p>
              Send this to the customer's Microsoft IT admin. Once they consent,
              the redirect URL contains <code>tenant=…</code> — that's their
              Entra Tenant ID for this config.
            </p>
            <p
              style={{
                wordBreak: "break-all",
                background: "#f5f5f5",
                padding: 8,
                borderRadius: 4,
              }}
            >
              {url}
            </p>
            <p style={{ color: "#888", marginBottom: 0 }}>
              {copied
                ? "(Copied to your clipboard.)"
                : "(Select and copy the link above.)"}
            </p>
          </div>
        ),
      });
    });
  };

  const runTest = (config) => {
    const hide = message.loading("Testing Microsoft reachability…", 0);
    entraSsoService
      .test(config.id)
      .then((res) => {
        const checks = (res && res.checks) || [];
        const content = (
          <div>
            {checks.map((c, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <span>{c.ok ? "✅" : "❌"}</span> <strong>{c.name}</strong>
                {c.detail ? (
                  <div style={{ color: "#888", marginLeft: 22 }}>{c.detail}</div>
                ) : null}
              </div>
            ))}
            {res && res.message ? (
              <p style={{ marginTop: 8, marginBottom: 0 }}>{res.message}</p>
            ) : null}
          </div>
        );
        const show = res && res.ok ? Modal.success : Modal.error;
        show({
          title: res && res.ok ? "Connection OK" : "Connection issues",
          width: 560,
          content,
        });
      })
      .catch((e) => {
        console.error("Entra test failed:", e);
        Modal.error({
          title: "Request failed",
          content: "Could not run the test. Please try again.",
        });
      })
      .finally(hide);
  };

  const confirmRemove = (id) => {
    entraSsoService.destroy(id).then((res) => {
      if (res && res.code === "0000") {
        getConfigs();
      }
    });
  };

  const openModal = () => {
    setEditingConfig({});
    setVisibleModal(true);
  };

  return (
    <LayoutWrapper>
      <EntraSsoWrapper>
        <PageHeader>Entra SSO Configuration</PageHeader>
        <div className="button-group">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            className="btn-success"
            onClick={openModal}
          >
            Add configuration
          </Button>
        </div>
        <Box id="containerBox">
          <Table
            rowKey="id"
            dataSource={dataSource}
            columns={columns}
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 12 }}
          />
        </Box>
      </EntraSsoWrapper>
      <EntraSsoModal
        visibleModal={visibleModal}
        setVisibleModal={setVisibleModal}
        getConfigs={getConfigs}
        editingConfig={editingConfig}
        setEditingConfig={setEditingConfig}
        corporateGroups={corporateGroups}
        properties={properties}
        roleTypes={roleTypes}
      />
    </LayoutWrapper>
  );
};

export default EntraSso;
